import { useState, useEffect } from 'react';
import { globalDeadlinesService } from '../lib/global-deadlines-service';
import { useFamilyContext } from '../contexts/FamilyContext';

// Hook to manage global deadlines with database persistence
export const useGlobalDeadlines = () => {
  const { family, currentUser } = useFamilyContext();
  const [globalDeadlines, setGlobalDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load global deadlines from database
  const loadGlobalDeadlines = async () => {
    if (!family?.id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      let deadlines = await globalDeadlinesService.getGlobalDeadlines(family.id);
      
      // If no deadlines exist, seed default ones and migrate from localStorage
      if (deadlines.length === 0) {
        await migrateAndSeedDeadlines();
        deadlines = await globalDeadlinesService.getGlobalDeadlines(family.id);
      }
      
      // Filter out only very old deadlines (more than 30 days past due)
      // Keep recent overdue items for proper urgent handling
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Start of today
      const thirtyDaysAgo = new Date(today.getTime() - (30 * 24 * 60 * 60 * 1000));

      const relevantDeadlines = deadlines.filter(deadline => {
        if (!deadline.due_date) return false;
        const dueDate = new Date(deadline.due_date);
        // Keep deadlines that are either in the future OR overdue by less than 30 days
        return dueDate >= thirtyDaysAgo;
      });

      console.log(`Filtered ${deadlines.length - relevantDeadlines.length} very old deadlines, keeping recent overdue items`);
      setGlobalDeadlines(relevantDeadlines);
    } catch (err) {
      console.error('Error loading global deadlines:', err);
      setError(err.message);
      // Fallback to localStorage if database fails
      loadFromLocalStorageFallback();
    } finally {
      setLoading(false);
    }
  };

  // Migrate localStorage data and seed defaults
  const migrateAndSeedDeadlines = async () => {
    if (!family?.id || !currentUser?.id) return;

    try {
      // Check if there's existing localStorage data to migrate
      const localData = localStorage.getItem('globalDeadlines');
      if (localData) {
        try {
          const localDeadlines = JSON.parse(localData);
          if (localDeadlines.length > 0) {
            // Migrate localStorage data to database
            for (const deadline of localDeadlines) {
              await globalDeadlinesService.createGlobalDeadline(
                {
                  title: deadline.title,
                  description: deadline.description,
                  due_date: deadline.due_date,
                  type: deadline.type || 'general'
                },
                family.id,
                currentUser.id
              );
            }
            // Clear localStorage after successful migration
            localStorage.removeItem('globalDeadlines');
            return;
          }
        } catch (e) {
          console.warn('Error migrating localStorage data:', e);
        }
      }

      // If no localStorage data, seed with defaults
      await globalDeadlinesService.seedDefaultGlobalDeadlines(family.id, currentUser.id);
    } catch (error) {
      console.error('Error migrating/seeding deadlines:', error);
      // Don't throw - this is non-critical, fallback to localStorage or empty state
      console.warn('⚠️ Using fallback mode for global deadlines');
    }
  };

  // Fallback to localStorage if database fails
  const loadFromLocalStorageFallback = () => {
    try {
      const saved = localStorage.getItem('globalDeadlines');
      if (saved) {
        const localDeadlines = JSON.parse(saved);
        
        // Filter out only very old deadlines from localStorage too
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const thirtyDaysAgo = new Date(today.getTime() - (30 * 24 * 60 * 60 * 1000));

        const relevantDeadlines = localDeadlines.filter(deadline => {
          if (!deadline.due_date) return false;
          const dueDate = new Date(deadline.due_date);
          return dueDate >= thirtyDaysAgo;
        });

        setGlobalDeadlines(relevantDeadlines);
      }
    } catch (e) {
      console.error('Error loading localStorage fallback:', e);
    }
  };

  // Load deadlines when family/user changes
  useEffect(() => {
    if (family?.id && currentUser?.id) {
      loadGlobalDeadlines();
    }
  }, [family?.id, currentUser?.id]);

  // Set up real-time subscription
  useEffect(() => {
    if (!family?.id) return;

    const subscription = globalDeadlinesService.subscribeToGlobalDeadlines(
      family.id,
      (payload) => {
        console.log('Global deadlines changed:', payload);
        // Only update if the change came from another client/session
        // Don't reload if we just made the change ourselves (optimistic updates handle that)
        if (payload.eventType === 'INSERT') {
          // Add the new deadline if it doesn't exist locally
          setGlobalDeadlines(prev => {
            const exists = prev.some(d => d.id === payload.new.id);
            if (!exists) {
              return [...prev, payload.new];
            }
            return prev;
          });
        } else if (payload.eventType === 'DELETE') {
          // Remove the deadline
          setGlobalDeadlines(prev => prev.filter(d => d.id !== payload.old.id));
        } else if (payload.eventType === 'UPDATE') {
          // Update the deadline
          setGlobalDeadlines(prev => 
            prev.map(d => d.id === payload.new.id ? payload.new : d)
          );
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [family?.id]);

  const addGlobalDeadline = async (newDeadline) => {
    if (!family?.id || !currentUser?.id) {
      throw new Error('Family or user not available');
    }

    try {
      const deadline = await globalDeadlinesService.createGlobalDeadline(
        newDeadline,
        family.id,
        currentUser.id
      );
      
      // Optimistically update local state
      setGlobalDeadlines(prev => [...prev, deadline]);
      return deadline;
    } catch (error) {
      console.error('Error adding global deadline:', error);
      throw error;
    }
  };

  const deleteGlobalDeadline = async (deadlineId) => {
    try {
      await globalDeadlinesService.deleteGlobalDeadline(deadlineId);
      
      // Optimistically update local state
      setGlobalDeadlines(prev => prev.filter(d => d.id !== deadlineId));
    } catch (error) {
      console.error('Error deleting global deadline:', error);
      throw error;
    }
  };

  const deleteGlobalDeadlineByIndex = (index) => {
    const deadline = globalDeadlines[index];
    if (deadline?.id) {
      return deleteGlobalDeadline(deadline.id);
    }
  };

  const updateGlobalDeadline = async (deadlineId, updates) => {
    try {
      const updatedDeadline = await globalDeadlinesService.updateGlobalDeadline(deadlineId, updates);
      
      // Optimistically update local state
      setGlobalDeadlines(prev => 
        prev.map(d => d.id === deadlineId ? updatedDeadline : d)
      );
      
      return updatedDeadline;
    } catch (error) {
      console.error('Error updating global deadline:', error);
      throw error;
    }
  };

  return {
    globalDeadlines,
    loading,
    error,
    addGlobalDeadline,
    deleteGlobalDeadline,
    deleteGlobalDeadlineByIndex,
    updateGlobalDeadline,
    refreshGlobalDeadlines: loadGlobalDeadlines
  };
}; 